import { useState, useEffect, useCallback, useRef } from "react";
import { AdminCard, AdminCardHead } from "components/admin/ui";
import { adminGet, adminPostForm, adminPatch, adminDelete } from "lib/adminClient";
import { useResource } from "hooks/useResource";
import { getInstitutions } from "services/content";
import { mediaUrl } from "lib/media";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/** Date -> "YYYY-MM-DD" in LOCAL time (toISOString would shift a day at +06:00). */
const toDateInput = (d) => {
  const x = new Date(d);
  return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

/**
 * Institution picker for a notice's tags. Options come from the institutions
 * content blob, so the tag always matches an institution's `noticeTag` exactly
 * — typing them by hand silently produced notices that appeared nowhere.
 * Multiple institutions are allowed; none means the notice is site-wide.
 */
function TagPicker({ value, onChange, compact }) {
  const { data } = useResource(getInstitutions);
  const options = (data?.items || [])
    .map((i) => ({ tag: i.noticeTag || i.short, name: i.name, color: i.color }))
    .filter((o) => o.tag);
  const available = options.filter((o) => !value.includes(o.tag));

  const nameFor = (tag) => options.find((o) => o.tag === tag)?.name || tag;
  const colorFor = (tag) => options.find((o) => o.tag === tag)?.color || "#005C40";

  return (
    <div className={`flex items-center gap-2 flex-wrap ${compact ? "" : "mb-3"}`}>
      <span className="text-[12px] font-semibold text-muted">Institutions</span>

      {value.map((t) => (
        <span
          key={t}
          title={nameFor(t)}
          className="inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1.5 py-1 text-[11.5px] font-semibold"
          style={{ background: `${colorFor(t)}18`, color: colorFor(t) }}
        >
          {t}
          <button
            onClick={() => onChange(value.filter((x) => x !== t))}
            aria-label={`Remove ${t}`}
            className="bg-transparent border-none cursor-pointer text-[13px] leading-none p-0 opacity-70 hover:opacity-100"
            style={{ color: colorFor(t) }}
          >
            ×
          </button>
        </span>
      ))}

      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => e.target.value && onChange([...value, e.target.value])}
          className="px-2.5 py-1.5 border-[1.5px] border-line rounded-lg font-sans text-[12.5px] outline-none focus:border-green bg-white cursor-pointer"
        >
          <option value="">+ Add institution…</option>
          {available.map((o) => (
            <option key={o.tag} value={o.tag}>
              {o.name}
            </option>
          ))}
        </select>
      )}

      {value.length === 0 && (
        <span className="text-[11.5px] text-muted">none selected — shows on every institution page</span>
      )}
    </div>
  );
}

export default function NoticesPage() {
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [draftTags, setDraftTags] = useState([]);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setList(await adminGet("/admin/notices"));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("text", draft.trim());
      if (draftTags.length) form.append("tags", draftTags.join(","));
      if (file) form.append("file", file);
      await adminPostForm("/admin/notices", form);
      setDraft("");
      setDraftTags([]);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pin = async (n) => {
    try {
      await adminPatch(`/admin/notices/${n.id}`, { pinned: !n.pinned });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const del = async (id) => {
    try {
      await adminDelete(`/admin/notices/${id}`);
      setList((l) => l.filter((n) => n.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const startEdit = (n) => {
    setError("");
    setEditId(n.id);
    setEditText(n.text);
    setEditDate(toDateInput(n.publishedAt));
    setEditTags(n.tags || []);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditText("");
    setEditDate("");
    setEditTags([]);
  };

  const saveEdit = async (n) => {
    const text = editText.trim();
    if (text.length < 3) {
      setError("Notice text must be at least 3 characters.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = { text, tags: editTags };
      // Only send the date if it actually changed — otherwise the stored time
      // of day would be reset to midnight on every text-only edit.
      if (editDate && editDate !== toDateInput(n.publishedAt)) {
        body.publishedAt = new Date(`${editDate}T12:00:00`).toISOString();
      }
      await adminPatch(`/admin/notices/${n.id}`, body);
      cancelEdit();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <AdminCard className="p-[22px] mb-5">
        <div className="font-bold text-[15px] text-green-dark mb-3.5">➕ Publish a New Notice</div>
        <div className="flex gap-3 mb-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Type the notice text here..."
            className="flex-1 px-4 py-3 border-[1.5px] border-line rounded-[9px] font-sans text-sm outline-none focus:border-green"
          />
          <button
            onClick={add}
            disabled={busy}
            className="bg-green text-white border-none rounded-[9px] px-7 font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {busy ? "Publishing…" : "Publish"}
          </button>
        </div>
        <TagPicker value={draftTags} onChange={setDraftTags} />
        <div className="flex items-center gap-3 text-[13px] text-muted">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 bg-[#f2f4f6] border border-line rounded-lg px-3 py-2 cursor-pointer hover:border-green transition-colors"
          >
            📎 {file ? "Change PDF" : "Attach PDF (optional)"}
          </button>
          {file && (
            <span className="flex items-center gap-2">
              <span className="font-medium text-ink">{file.name}</span>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className="text-[#c44] bg-transparent border-none cursor-pointer"
                title="Remove"
              >
                ✕
              </button>
            </span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>
      </AdminCard>

      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard>
        <AdminCardHead
          title={`All Notices (${list.length})`}
          action={<span className="text-xs text-[#9aa]">📌 Pinned show first · 📎 = PDF attached</span>}
        />
        <div>
          {loading ? (
            <div className="px-[22px] py-8 text-center text-muted text-sm">Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-[22px] py-8 text-center text-muted text-sm">No notices yet.</div>
          ) : (
            list.map((n, i) => (
              <div
                key={n.id}
                className={`flex gap-3.5 px-[22px] py-[15px] ${
                  editId === n.id ? "items-start" : "items-center"
                }`}
                style={{
                  borderBottom: i < list.length - 1 ? "1px solid #f2f4f6" : "none",
                  background: n.pinned ? "#fffdf5" : "transparent",
                }}
              >
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: n.pinned ? "#9B7200" : "#005C40" }}
                />
                {editId === n.id ? (
                  <>
                    <div className="flex-1 flex flex-col gap-2">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") cancelEdit();
                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit(n);
                        }}
                        rows={2}
                        autoFocus
                        className="w-full px-3 py-2 border-[1.5px] border-green rounded-lg font-sans text-sm outline-none resize-y"
                      />
                      <TagPicker value={editTags} onChange={setEditTags} compact />
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-[11.5px] text-muted">Published</label>
                        <input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          className="px-2.5 py-1.5 border-[1.5px] border-line rounded-lg font-sans text-[12.5px] outline-none focus:border-green"
                        />
                        {n.fileUrl && (
                          <span className="text-[11.5px] text-muted">
                            · 📎 PDF attached (delete &amp; re-publish to change it)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => saveEdit(n)}
                      disabled={busy}
                      title="Save changes"
                      className="bg-green text-white border-none rounded-[7px] px-3.5 py-[7px] cursor-pointer text-[13px] font-semibold disabled:opacity-60"
                    >
                      {busy ? "…" : "Save"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      title="Cancel (Esc)"
                      className="bg-[#f2f4f6] border-none rounded-[7px] px-3 py-[7px] cursor-pointer text-[13px]"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1">
                      <div className="text-sm text-ink">{n.text}</div>
                      <div className="text-[11.5px] text-[#9aa] mt-0.5 flex items-center gap-2">
                        <span>{fmtDate(n.publishedAt)}</span>
                        {n.pinned && <span>· 📌 Pinned</span>}
                        {n.tags?.length > 0 && (
                          <span className="text-green font-semibold">· 🏷 {n.tags.join(", ")}</span>
                        )}
                        {n.fileUrl && (
                          <a
                            href={mediaUrl(n.fileUrl)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-green font-semibold no-underline hover:underline"
                          >
                            · 📎 View PDF
                          </a>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => startEdit(n)}
                      title="Edit"
                      className="bg-[#f2f4f6] border-none rounded-[7px] px-[11px] py-[7px] cursor-pointer text-[13px]"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => pin(n)}
                      title={n.pinned ? "Unpin" : "Pin"}
                      className="border-none rounded-[7px] px-[11px] py-[7px] cursor-pointer text-[13px]"
                      style={{ background: n.pinned ? "#f7efc8" : "#f2f4f6" }}
                    >
                      📌
                    </button>
                    <button
                      onClick={() => del(n.id)}
                      title="Delete"
                      className="bg-[#fdeaea] border-none rounded-[7px] px-[11px] py-[7px] cursor-pointer text-[13px] text-[#c44]"
                    >
                      🗑
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </AdminCard>
    </div>
  );
}
